import makeStyles from "@mui/styles/makeStyles";
import { ICellRendererParams } from "ag-grid-community";
import ArrowPopper from "components/app/ArrowPopper";
import { classHOC } from "components/app/ClassWrapper";
import useFilterModelHighlightHTML from "components/app/useFilterModelHighlightHTML";
import SubscriptionDialog from "components/dashboard/executive/cellRenderer/SubscriptionDialog";
import { useEffect, useRef, useState } from "react";

const useStyles = makeStyles(() => ({
  wrapper: {
    display: "flex",
    alignItems: "center",
  },
  info: {
    display: "flex",
    alignItems: "center",
  },
  main: {
    fontSize: 12,
    marginRight: 5,
    color: "#535759",
  },
  sub: {
    color: "#898989",
    fontSize: 10,
  },
  labelWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 20,
    borderRadius: 12,
    border: "1px solid #DEDEDE",
    marginLeft: 5,
  },
  txt: {
    fontSize: 11,
  },
  moreProducts: {
    padding: "10px 12px",
    width: 240,
    height: 108,
    overflowY: "auto",
    overflowX: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    border: "1px solid #DEDEDE",
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.16), 0px 0px 1px rgba(0, 0, 0, 0.14)",
    borderRadius: 4,
  },
  infoRow: {
    display: "flex",
    marginBottom: 10,
    "&:last-of-type": {
      marginBottom: 0,
    },
  },
}));

export interface SubscriptionType {
  subscriptionId: string;
  forecasting: string[];
  renewalFactors: string[];
  lifecycleStageByWorkload: string[];
}

function ChipRender({ list }: { list: string[] }) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const epPopRef = useRef<HTMLDivElement>(null);
  const epCellRef = useRef<HTMLDivElement>(null);
  const detailPopRef = useRef<HTMLDivElement>(null);

  const len = list.length;

  const showMore = (event: any) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        ((!epPopRef.current?.contains(e.target as Node) && epPopRef.current !== e.target) ||
          (!detailPopRef.current?.contains(e.target as Node) &&
            detailPopRef.current !== e.target)) &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  return (
    <div
      style={{ display: "flex", alignItems: "center" }}
      className="hover-show-container"
      ref={epCellRef}
    >
      {len === 0 && ""}
      {len === 1 && list.map((a) => <span key={a}>{a}</span>)}
      {len > 1 && (
        <>
          <span>{list[0]}</span>
          <div
            className={classes.labelWrapper}
            style={{ background: anchorEl ? "#333" : "#fff" }}
            onClick={showMore}
          >
            <p
              className={classes.txt}
              style={{
                color: anchorEl ? "#fff" : "#535759",
                cursor: "pointer",
              }}
            >
              + {len - 1}
            </p>
          </div>
          {anchorEl ? (
            <ArrowPopper
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              placement="right"
              disablePortal={false}
              disableFlip={true}
            >
              <div ref={detailPopRef}>
                {list?.slice(1).map((p: string) => {
                  return (
                    <div
                      className={classes.infoRow}
                      key={p}
                      style={{ alignItems: "center" }}
                    >
                      <p className={classes.main}>{p}</p>
                    </div>
                  );
                })}
              </div>
            </ArrowPopper>
          ) : null}
        </>
      )}
    </div>
  );
}

const SubscriptionRenderer = ({
  node,
  api,
  data,
  fieldKey,
  mappings,
}: ICellRendererParams & {
  fieldKey: "forecasting" | "renewalFactors" | "lifecycleStageByWorkload";
  mappings: { [key: string]: string };
}) => {
  if (node.rowPinned) return null;
  const [dialogStatus, setDialogStatus] = useState(false);
  const subscriptions: SubscriptionType[] = data.subscriptions?.filter(
    (s: SubscriptionType) => s[fieldKey].filter((f) => !!f).length > 0
  );
  const subLength = subscriptions?.length;

  if (fieldKey === "renewalFactors" && subLength > 0) {
    subscriptions.map((s) => {
      const newArr = Array.from(s[fieldKey]);
      s[fieldKey] = newArr.map((n) => (n in mappings ? mappings[n] : n));
    });
  }

  const handleDialogStatus = () => {
    setDialogStatus(true);
  };

  return (
    <>
      {subLength === 0 && <div>N/A</div>}
      {subLength === 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <ChipRender list={subscriptions[0][fieldKey]} />
        </div>
      )}
      {subLength > 1 && (
        <>
          <div
            onClick={handleDialogStatus}
            style={{ cursor: "pointer", color: "dodgerblue" }}
            dangerouslySetInnerHTML={{
              __html: useFilterModelHighlightHTML(api, `Show All (${subLength})`),
            }}
          />
          <SubscriptionDialog
            list={subscriptions}
            fieldKey={fieldKey}
            dialogStatus={dialogStatus}
            setDialogStatus={setDialogStatus}
          />
        </>
      )}
    </>
  );
};

export default classHOC(SubscriptionRenderer);
